import { useEffect, useState } from 'react';
import { useNavigate, useParams, useRevalidator } from 'react-router';
import { toast } from 'react-toastify';
import SettingsPanel from '@/components/core/SettingsPanel';
import LoadMoreButton from '@/components/core/LoadMoreButton';
import { confirm } from '@/components/core/createConfirmation';
import { getPathway, getPathwayRevisions, restorePathwayRevision } from '@/api/pathways';
import { useIsPathwayLocked, useParentPathwayId } from '@/stores/pathwayStore';
import { formatDate } from '@/utils/miscUtils';

const formatRevisionDate = date => formatDate(new Date(date), 'PPp');

// The axios interceptor (`initializers/xhr.js`) shows request errors itself, and
// resolves 403s with no response instead of rejecting.
const fetchRevisions = async (pathwayId, offset = 0) => {
  const response = await getPathwayRevisions(pathwayId, { offset });
  if (!response?.data) {
    throw new Error('Failed to load revisions');
  }
  return response.data;
};

const pathwayExists = pathwayId =>
  getPathway(pathwayId, {}, { suppressErrorToast: true }).then(
    () => true,
    () => false,
  );

const PathwayRevisions = () => {
  const { projectId, pathwayId } = useParams();
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const locked = useIsPathwayLocked();
  const isInternalPathway = !!useParentPathwayId();
  const [revisions, setRevisions] = useState([]);
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState('loading');
  const [reloadCount, setReloadCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    let ignore = false;

    fetchRevisions(pathwayId).then(
      data => {
        if (ignore) return;
        setRevisions(data.results);
        setCount(data.count);
        setStatus('loaded');
      },
      () => !ignore && setStatus('failed'),
    );

    return () => {
      ignore = true;
    };
  }, [pathwayId, reloadCount]);

  const onLoadMore = async () => {
    setIsLoadingMore(true);

    try {
      const data = await fetchRevisions(pathwayId, revisions.length);
      setRevisions(prev => [...prev, ...data.results]);
      setCount(data.count);
    } catch {
      // already shown by the axios interceptor
    } finally {
      setIsLoadingMore(false);
    }
  };

  const onRestore = async revision => {
    const date = formatRevisionDate(revision.created_at);
    const ok = await confirm(
      `Restore ${isInternalPathway ? 'the top-level pathway' : 'this pathway'} to how it was on ${date}? ` +
        "Balance and analysis results will need to be re-run. The pathway's current state stays in the history, so you can restore it later.",
    );

    if (!ok) {
      return;
    }

    setRestoringId(revision.id);

    try {
      const response = await restorePathwayRevision(pathwayId, revision.id);

      if (!response?.data) {
        toast.error("This revision couldn't be restored. Check that the pathway isn't locked.");
        return;
      }

      toast.success(`Restored the pathway to how it was on ${date}`);

      const { id: rootPathwayId, case_id: rootCaseId } = response.data;

      // restoring from inside an internal pathway restores the whole top-level
      // pathway, which can remove the internal pathway we're looking at
      if (rootPathwayId !== pathwayId && !(await pathwayExists(pathwayId))) {
        navigate(`/projects/${projectId}/build/pathways/${rootPathwayId}/${rootCaseId}/control-panel/revisions`, {
          replace: true,
        });
        return;
      }

      revalidate();
      setReloadCount(value => value + 1);
    } catch {
      // already shown by the axios interceptor
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <SettingsPanel title="Revisions">
      <div className="space-y-4">
        {locked && <p className="text-sm text-gray-500">This pathway is locked. Unlock it to restore a revision.</p>}

        {status === 'loading' && <p className="text-sm text-gray-500">Loading revisions...</p>}
        {status === 'failed' && <p className="text-sm text-red-500">Failed to load revisions.</p>}
        {status === 'loaded' && revisions.length === 0 && <p className="text-sm text-gray-500">No revisions yet.</p>}

        {status === 'loaded' && revisions.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 px-4 font-medium">Saved</th>
                <th className="py-2 px-4 font-medium">By</th>
                <th className="py-2 px-4 font-medium">Notes</th>
                <th className="py-2 px-4">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {revisions.map(revision => (
                <tr key={revision.id} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="py-2 px-4 whitespace-nowrap">{formatRevisionDate(revision.created_at)}</td>
                  <td className="py-2 px-4">
                    {revision.user?.name || revision.user?.email || 'Unknown user'}
                    {revision.source === 'agent' && <span className="badge badge-sm badge-ghost ml-2">AI agent</span>}
                  </td>
                  <td className="py-2 px-4 text-gray-500">
                    {revision.restored_from_created_at
                      ? `Restored from ${formatRevisionDate(revision.restored_from_created_at)}`
                      : revision.description}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {revision.is_current ? (
                      <span className="badge badge-sm">Current</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline"
                        disabled={locked || restoringId !== null}
                        onClick={() => onRestore(revision)}
                      >
                        {restoringId === revision.id ? 'Restoring...' : 'Restore'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <LoadMoreButton
          onLoadMore={onLoadMore}
          loading={isLoadingMore}
          hasMore={status === 'loaded' && revisions.length < count}
          remainingCount={count - revisions.length}
          className="btn btn-sm btn-outline"
        />
      </div>
    </SettingsPanel>
  );
};

export default PathwayRevisions;
